import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ProjectCard = ({ project, currentLanguage, onViewDetails, onShare }) => {
  const [showBefore, setShowBefore] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  const toggleImage = () => {
    setShowBefore(!showBefore);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const getServiceTypeLabel = (type) => {
    const labels = {
      'deep-cleaning': { es: 'Limpieza Profunda', en: 'Deep Cleaning' },
      'maintenance': { es: 'Mantenimiento', en: 'Maintenance' },
      'restoration': { es: 'Restauración', en: 'Restoration' },
      'commercial': { es: 'Comercial', en: 'Commercial' },
      'premium': { es: 'Premium', en: 'Premium' },
      'community': { es: 'Comunitario', en: 'Community' }
    };
    return labels[type] ? labels[type][currentLanguage] : type;
  };

  const getPoolSizeLabel = (size) => {
    const labels = {
      'small': { es: 'Pequeña', en: 'Small' },
      'medium': { es: 'Mediana', en: 'Medium' },
      'large': { es: 'Grande', en: 'Large' },
      'extra-large': { es: 'Extra Grande', en: 'Extra Large' }
    };
    return labels[size] ? labels[size][currentLanguage] : size;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return currentLanguage === 'es' ? date.toLocaleDateString('es-CR', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="bg-surface rounded-lg shadow-medium overflow-hidden hover:shadow-large nav-transition">
      {/* Image Container */}
      <div className="relative h-64 overflow-hidden group">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
            <Icon name="Image" size={32} className="text-gray-400" />
          </div>
        )}
        
        <Image
          src={showBefore ? project.beforeImage : project.afterImage}
          alt={currentLanguage === 'es' ? project.title : project.titleEn}
          className="w-full h-full object-cover"
          onLoad={handleImageLoad}
        />
        
        {/* Before/After Toggle */}
        <div className="absolute top-4 left-4">
          <button
            onClick={toggleImage}
            className={`px-3 py-1 rounded-full text-sm font-medium nav-transition ${
              showBefore 
                ? 'bg-error text-white' :'bg-success text-white'
            }`}
          >
            {showBefore 
              ? (currentLanguage === 'es' ? 'Antes' : 'Before')
              : (currentLanguage === 'es' ? 'Después' : 'After')
            }
          </button>
        </div>

        {/* Rating */}
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center space-x-1">
          <Icon name="Star" size={14} className="text-warning fill-current" />
          <span className="text-sm font-medium">{project.rating}</span>
        </div>

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 nav-transition flex items-center justify-center">
          <div className="flex space-x-2">
            <button
              onClick={() => onViewDetails(project)}
              className="bg-white text-primary px-4 py-2 rounded-lg font-medium hover:bg-gray-50 nav-transition flex items-center space-x-2"
            >
              <Icon name="Eye" size={16} />
              <span>{currentLanguage === 'es' ? 'Ver Detalles' : 'View Details'}</span>
            </button>
            <button
              onClick={() => onShare(project)}
              className="bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary/90 nav-transition flex items-center space-x-2"
            >
              <Icon name="Share2" size={16} />
              <span>{currentLanguage === 'es' ? 'Compartir' : 'Share'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-heading font-semibold text-text-primary line-clamp-2">
            {currentLanguage === 'es' ? project.title : project.titleEn}
          </h3>
        </div>

        {/* Project Info */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <span className="text-text-secondary">
              {currentLanguage === 'es' ? 'Servicio:' : 'Service:'}
            </span>
            <div className="font-medium text-text-primary">
              {getServiceTypeLabel(project.serviceType)}
            </div>
          </div>
          <div>
            <span className="text-text-secondary">
              {currentLanguage === 'es' ? 'Tamaño:' : 'Size:'}
            </span>
            <div className="font-medium text-text-primary">
              {getPoolSizeLabel(project.poolSize)}
            </div>
          </div>
          <div>
            <span className="text-text-secondary">
              {currentLanguage === 'es' ? 'Ubicación:' : 'Location:'}
            </span>
            <div className="font-medium text-text-primary">{project.location}</div>
          </div>
          <div>
            <span className="text-text-secondary">
              {currentLanguage === 'es' ? 'Duración:' : 'Duration:'}
            </span>
            <div className="font-medium text-text-primary">
              {currentLanguage === 'es' ? project.duration : project.durationEn}
            </div>
          </div>
        </div>

        {/* Description Preview */}
        <p className="text-text-secondary text-sm mb-4 line-clamp-3">
          {currentLanguage === 'es' ? project.description.split('\n')[0]
            : project.descriptionEn.split('\n')[0]
          }
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-text-secondary">
            {formatDate(project.completionDate)}
          </div>
          <div className="flex items-center space-x-2 text-sm text-text-secondary">
            <Icon name="User" size={14} />
            <span>{project.customerName}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;